import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Package, Plus, Upload } from 'lucide-react';
import { listMasterItems, importMasterItems } from '../../api/catalog';
import { useMasterItemImageUrl } from '../../hooks/useCatalogItemImageUrl';
import { PageHeader } from '../../components/ui/PageHeader';
import { Card } from '../../components/ui/Card';
import { Table, Thead, Th, Tr, Td } from '../../components/ui/Table';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/Button';
import { CsvImportModal } from '../../components/CsvImportModal';
import { MasterItemModal } from './MasterItemModal';
import type { MasterCatalogItem } from '../../types/api';

function money(value: string | null) {
  return value ? `£${Number(value).toFixed(2)}` : '-';
}

function ItemThumbnail({ item }: { item: MasterCatalogItem }) {
  const url = useMasterItemImageUrl(item.id, item.hasImage);
  return (
    <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
      {url ? <img src={url} alt={item.name} className="h-full w-full object-cover" /> : <Package className="h-4 w-4 text-slate-300" />}
    </div>
  );
}

// MSP-wide catalog templates - the starting point Customer Admins build
// their own site-specific catalog from (CustomerCatalogItem).
export function MasterCatalogPage() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MasterCatalogItem | null>(null);
  const itemsQuery = useQuery({ queryKey: ['master-items'], queryFn: listMasterItems });

  const editingItem = editTarget ? itemsQuery.data?.find((i) => i.id === editTarget.id) ?? editTarget : null;

  return (
    <div>
      <PageHeader
        title="Master Catalog"
        description="Shared templates Customer Admins can base their own customer catalog items on."
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => setImportOpen(true)}>
              <Upload className="h-3.5 w-3.5" /> Import CSV
            </Button>
            <Button size="sm" onClick={() => setModalOpen(true)}>
              <Plus className="h-3.5 w-3.5" /> New item
            </Button>
          </div>
        }
      />

      <Card>
        {itemsQuery.isLoading ? (
          <div className="py-14 text-center">
            <Spinner />
          </div>
        ) : !itemsQuery.data || itemsQuery.data.length === 0 ? (
          <EmptyState icon={Package} title="No master items yet" description="Add a template item to get started." />
        ) : (
          <Table>
            <Thead>
              <tr>
                <Th></Th>
                <Th>Name</Th>
                <Th>Category</Th>
                <Th>Default monthly</Th>
                <Th>Default total</Th>
                <Th></Th>
              </tr>
            </Thead>
            <tbody>
              {itemsQuery.data.map((item) => (
                <Tr key={item.id} className="cursor-pointer" onClick={() => setEditTarget(item)}>
                  <Td>
                    <ItemThumbnail item={item} />
                  </Td>
                  <Td className="font-medium text-slate-900">{item.name}</Td>
                  <Td>{item.category ?? <span className="text-slate-400">-</span>}</Td>
                  <Td>{money(item.defaultCostMonthly)}</Td>
                  <Td>{money(item.defaultCostTotal)}</Td>
                  <Td className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditTarget(item);
                      }}
                    >
                      Edit
                    </Button>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <MasterItemModal open={modalOpen} onClose={() => setModalOpen(false)} />
      <MasterItemModal open={!!editTarget} onClose={() => setEditTarget(null)} item={editingItem} />
      <CsvImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Import master catalog items"
        description="Add or update master items in bulk. Matches existing items by exact name, so re-running an updated file is safe."
        templateFilename="master-catalog-template.csv"
        templateHeaders={['name', 'description', 'category', 'defaultCostMonthly', 'defaultCostTotal']}
        templateExampleRow={['Dell Latitude 5440', '14" business laptop', 'Laptop', '25', '900']}
        importFn={importMasterItems}
        onImported={() => queryClient.invalidateQueries({ queryKey: ['master-items'] })}
      />
    </div>
  );
}
